<template>
  <div>
    <Navbar activeDirectory="admin" activePage="admin-strade"></Navbar>

    <div class="container-fluid py-4">

      <!-- ===== PAGE HEADER ===== -->
      <div class="d-flex align-items-center justify-content-between flex-wrap mb-4" style="gap:12px;">
        <div class="d-flex align-items-center">
          <div class="au-header-icon mr-3">
            <i class="mdi mdi-road-variant"></i>
          </div>
          <div class="text-left">
            <h3 class="mb-0 font-weight-bold text-dark">Gestione Cantieri</h3>
            <p class="mb-0 text-muted small">Aggiungi, modifica o rimuovi i cantieri stradali per le commesse</p>
          </div>
        </div>
        <div class="d-flex align-items-center" style="gap:12px;">
          <!-- Commessa selector -->
          <div class="cw-select-wrap" style="min-width:160px;" v-if="mode === 'read'">
            <i class="mdi mdi-briefcase-outline cw-field-icon"></i>
            <select v-model="selectedLotto" class="cw-select" @change="fetchRoads">
              <option v-for="c in commesse" :key="c.id" :value="String(c.id)">{{ c.nome }}</option>
            </select>
            <i class="mdi mdi-chevron-down cw-select-arrow"></i>
          </div>
          <button v-if="mode === 'read'" class="au-btn-submit" @click="setAddMode">
            <i class="mdi mdi-map-marker-plus mr-1"></i> Aggiungi Cantiere
          </button>
          <button v-else class="btn btn-light rounded-pill px-4 font-weight-semibold border" @click="mode = 'read'">
            <i class="mdi mdi-arrow-left mr-1"></i> Torna all'elenco
          </button>
        </div>
      </div>

      <!-- ===== LIST MODE ===== -->
      <div v-if="mode === 'read'">
        <div class="au-card">
          <div class="cw-section-title">
            Elenco Cantieri
          </div>

          <!-- Search -->
          <div class="mb-4" style="max-width:360px;">
            <div class="au-search-wrap">
              <i class="mdi mdi-magnify au-search-icon"></i>
              <input v-model="searchQuery" class="au-search-input" placeholder="Cerca cantiere per nome...">
            </div>
          </div>

          <!-- Table -->
          <div class="table-responsive">
            <table class="au-table">
              <thead>
                <tr>
                  <th style="width:70px;">ID</th>
                  <th>Nome Strada / Cantiere</th>
                  <th>Commessa</th>
                  <th>Posizione Inizio</th>
                  <th>Posizione Fine</th>
                  <th class="text-right" style="width:180px;">Azioni</th>
                </tr>
              </thead>
              <tbody>
                <!-- Loading -->
                <tr v-if="loadingRoads">
                  <td colspan="6" class="text-center py-5">
                    <span class="spinner-border spinner-border-sm text-primary mr-2"></span>
                    Caricamento cantieri...
                  </td>
                </tr>
                <!-- Empty -->
                <tr v-else-if="filteredRoads.length === 0">
                  <td colspan="6" class="text-center py-5 text-muted">
                    <i class="mdi mdi-road mdi-48px d-block mb-2"></i>
                    Nessun cantiere trovato per questa commessa.
                  </td>
                </tr>
                <!-- Rows -->
                <tr v-for="item in filteredRoads" :key="item.id" v-else>
                  <td class="font-weight-bold text-muted">#{{ item.id }}</td>
                  <td>
                    <div class="d-flex align-items-center">
                      <div class="as-road-icon mr-3"><i class="mdi mdi-road"></i></div>
                      <span class="font-weight-bold text-dark">{{ item.nome }}</span>
                    </div>
                  </td>
                  <td>
                    <span class="au-role-badge au-role-admin">{{ getCommessaNome(item.lotto) }}</span>
                  </td>
                  <td>
                    <span v-if="item.lat_start" class="small text-muted font-monospace">
                      {{ item.lat_start.toFixed(5) }}, {{ item.lon_start.toFixed(5) }}
                    </span>
                    <span v-else class="text-muted">—</span>
                  </td>
                  <td>
                    <span v-if="item.lat_end" class="small text-muted font-monospace">
                      {{ item.lat_end.toFixed(5) }}, {{ item.lon_end.toFixed(5) }}
                    </span>
                    <span v-else class="text-muted">—</span>
                  </td>
                  <td class="text-right text-nowrap">
                    <button class="au-btn-action mr-2" @click="setEditMode(item)">
                      <i class="mdi mdi-pencil-outline mr-1"></i> Modifica
                    </button>
                    <button class="au-btn-danger-sm" @click="deleteRoad(item)">
                      <i class="mdi mdi-trash-can-outline mr-1"></i> Elimina
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- ===== ADD / EDIT MODE ===== -->
      <div v-if="mode === 'add' || mode === 'edit'">
        <form @submit.prevent="saveRoad">
          <div class="row">

            <!-- ─── Col sx: Dati del cantiere ─── -->
            <div class="col-12 col-lg-5 mb-4">
              <div class="au-card h-100">
                <div class="cw-section-title">
                  <span class="cw-step-num">1</span>{{ mode === 'add' ? 'Nuovo Cantiere' : 'Modifica Cantiere' }}
                </div>

                <!-- Commessa -->
                <div class="form-group mb-4">
                  <label class="cw-label">Commessa <span class="text-danger">*</span></label>
                  <div class="cw-select-wrap">
                    <i class="mdi mdi-briefcase-outline cw-field-icon"></i>
                    <select v-model="roadForm.lotto" class="cw-select" required>
                      <option v-for="c in commesse" :key="c.id" :value="String(c.id)">{{ c.nome }}</option>
                    </select>
                    <i class="mdi mdi-chevron-down cw-select-arrow"></i>
                  </div>
                </div>

                <!-- Nome -->
                <div class="form-group mb-4">
                  <label class="cw-label">Nome Strada / Cantiere <span class="text-danger">*</span></label>
                  <div class="cw-input-wrap">
                    <i class="mdi mdi-sign-direction cw-field-icon"></i>
                    <input v-model="roadForm.nome" class="cw-input" placeholder="Es. Via Roma" required>
                  </div>
                </div>

                <!-- Tipo posizione toggle -->
                <div class="form-group mb-4">
                  <label class="cw-label">Tipo Posizione</label>
                  <div class="cw-location-tabs">
                    <button type="button" class="cw-loc-tab" :class="{ active: coordType === 'point' }" @click="setCoordType('point')">
                      <i class="mdi mdi-map-marker"></i><span>Punto Singolo</span>
                    </button>
                    <button type="button" class="cw-loc-tab" :class="{ active: coordType === 'segment' }" @click="setCoordType('segment')">
                      <i class="mdi mdi-vector-line"></i><span>Segmento</span>
                    </button>
                  </div>
                </div>

                <!-- Coordinate inizio -->
                <div class="cw-section-title mt-2" style="font-size:0.72rem;">
                  Punto di Inizio
                </div>
                <div class="row mb-3">
                  <div class="col-6 form-group mb-0">
                    <label class="cw-label">Latitudine <span class="text-danger">*</span></label>
                    <div class="cw-input-wrap">
                      <i class="mdi mdi-latitude cw-field-icon"></i>
                      <input type="text" inputmode="decimal" :value="roadForm.lat_start" @input="onCoordInput('lat_start', $event.target.value)" class="cw-input" placeholder="40.8518" required>
                    </div>
                  </div>
                  <div class="col-6 form-group mb-0">
                    <label class="cw-label">Longitudine <span class="text-danger">*</span></label>
                    <div class="cw-input-wrap">
                      <i class="mdi mdi-longitude cw-field-icon"></i>
                      <input type="text" inputmode="decimal" :value="roadForm.lon_start" @input="onCoordInput('lon_start', $event.target.value)" class="cw-input" placeholder="14.2681" required>
                    </div>
                  </div>
                </div>

                <!-- Coordinate fine (segmento) -->
                <div v-if="coordType === 'segment'">
                  <div class="cw-section-title mt-2" style="font-size:0.72rem;">
                    Punto di Fine
                  </div>
                  <div class="row mb-3">
                    <div class="col-6 form-group mb-0">
                      <label class="cw-label">Latitudine <span class="text-danger">*</span></label>
                      <div class="cw-input-wrap">
                        <i class="mdi mdi-latitude cw-field-icon"></i>
                        <input type="text" inputmode="decimal" :value="roadForm.lat_end" @input="onCoordInput('lat_end', $event.target.value)" class="cw-input" placeholder="40.8525" required>
                      </div>
                    </div>
                    <div class="col-6 form-group mb-0">
                      <label class="cw-label">Longitudine <span class="text-danger">*</span></label>
                      <div class="cw-input-wrap">
                        <i class="mdi mdi-longitude cw-field-icon"></i>
                        <input type="text" inputmode="decimal" :value="roadForm.lon_end" @input="onCoordInput('lon_end', $event.target.value)" class="cw-input" placeholder="14.2690" required>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Click target helper (segmento) -->
                <div v-if="roadForm.lat_start && coordType === 'segment'" class="cw-alert-info d-flex align-items-center mt-3">
                  <i class="mdi mdi-gesture-tap mr-2 text-primary h5 mb-0"></i>
                  <div class="small">
                    Click sulla mappa imposta: &nbsp;
                    <span class="cw-chip mr-1" :class="{ 'cw-chip-active': clickTarget === 'start' }" @click.prevent="clickTarget = 'start'" style="cursor:pointer;">Inizio</span>
                    <span class="cw-chip" :class="{ 'cw-chip-active': clickTarget === 'end' }" @click.prevent="clickTarget = 'end'" style="cursor:pointer;">Fine</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- ─── Col dx: Geolocalizzazione + Mappa ─── -->
            <div class="col-12 col-lg-7 mb-4">
              <div class="au-card h-100 d-flex flex-column">
                <div class="cw-section-title">
                  <span class="cw-step-num">2</span>Geolocalizzazione
                </div>

                <!-- OSM Search -->
                <div class="form-group mb-4">
                  <label class="cw-label">Cerca indirizzo su OpenStreetMap</label>
                  <div class="as-osm-search-wrap position-relative">
                    <div class="cw-input-wrap">
                      <i class="mdi mdi-text-search cw-field-icon"></i>
                      <input
                        v-model="addressSearchQuery"
                        class="cw-input"
                        placeholder="Es. Via Roma, Napoli"
                        @keydown.enter.prevent="searchOSMAddress"
                        style="padding-right: 110px;"
                      >
                      <button
                        class="as-osm-search-btn"
                        type="button"
                        @click="searchOSMAddress"
                        :disabled="searchingOSM"
                      >
                        <span class="spinner-border spinner-border-sm mr-1" v-if="searchingOSM"></span>
                        <i class="mdi mdi-magnify mr-1" v-else></i>Cerca
                      </button>
                    </div>
                    <!-- Suggestions -->
                    <div class="road-dropdown-menu shadow-lg border-0" v-if="osmSuggestions.length > 0">
                      <div
                        class="road-dropdown-item py-2 px-3 d-flex align-items-center"
                        v-for="(sug, index) in osmSuggestions"
                        :key="index"
                        @click="selectOSMSuggestion(sug)"
                      >
                        <span class="text-truncate">{{ sug.display_name }}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Map -->
                <div class="as-map-wrapper flex-grow-1">
                  <l-map
                    style="height: 100%; width: 100%"
                    :zoom="zoom"
                    :center="center"
                    @click="handleMapClick"
                    ref="roadMap"
                  >
                    <l-tile-layer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"></l-tile-layer>

                    <!-- Start Marker -->
                    <l-marker
                      v-if="startLatLng"
                      :lat-lng="startLatLng"
                      :draggable="true"
                      @update:lat-lng="handleStartDrag"
                    >
                      <l-icon :icon-size="[30, 42]" :icon-anchor="[15, 42]" class-name="bg-transparent border-0">
                        <div class="marker-pin bg-success">
                          <span class="text-white font-weight-bold small" style="font-size:11px;">I</span>
                        </div>
                      </l-icon>
                    </l-marker>

                    <!-- End Marker -->
                    <l-marker
                      v-if="endLatLng && coordType === 'segment'"
                      :lat-lng="endLatLng"
                      :draggable="true"
                      @update:lat-lng="handleEndDrag"
                    >
                      <l-icon :icon-size="[30, 42]" :icon-anchor="[15, 42]" class-name="bg-transparent border-0">
                        <div class="marker-pin bg-danger">
                          <span class="text-white font-weight-bold small" style="font-size:11px;">F</span>
                        </div>
                      </l-icon>
                    </l-marker>

                    <!-- Polyline -->
                    <l-polyline
                      v-if="polylinePath.length > 1 && coordType === 'segment'"
                      :lat-lngs="polylinePath"
                      color="#F7931E"
                      :weight="4"
                    ></l-polyline>
                  </l-map>
                </div>

                <!-- Map hint -->
                <div class="cw-alert-info d-flex align-items-center mt-3">
                  <i class="mdi mdi-gesture-tap mr-2 text-primary h5 mb-0"></i>
                  <div class="small text-muted">
                    Clicca sulla mappa per impostare le coordinate.
                    I marker sono trascinabili per aggiustare la posizione.
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="cw-footer d-flex align-items-center justify-content-between flex-wrap">
            <small class="text-muted">
              <i class="mdi mdi-asterisk text-danger" style="font-size:0.6rem; vertical-align:middle;"></i>
              Campi obbligatori
            </small>
            <div class="d-flex" style="gap:12px;">
              <button type="button" class="btn btn-light rounded-pill px-4 font-weight-semibold border" @click="mode = 'read'">
                Annulla
              </button>
              <button type="submit" class="au-btn-submit" :disabled="submitting">
                <span class="spinner-border spinner-border-sm mr-2" v-if="submitting"></span>
                <i class="mdi mdi-check-circle-outline mr-1" v-else></i>
                Salva Cantiere
              </button>
            </div>
          </div>
        </form>
      </div>

    </div>
  </div>
</template>

<script>
import { Navbar } from "@/components";
import { contextStore } from "@/utils/contextStore";
import { latLng } from "leaflet";

export default {
  components: {
    Navbar,
  },

  data() {
    return {
      tipologia: localStorage.getItem("tipologia"),
      roads: [],
      loadingRoads: false,
      mode: "read", // 'read', 'add', 'edit'

      roadForm: {
        id: null,
        nome: "",
        lotto: "1",
        tronco: "",
        lat_start: null,
        lon_start: null,
        lat_end: null,
        lon_end: null
      },

      coordType: "point", // 'point', 'segment'
      clickTarget: "start", // 'start', 'end'
      submitting: false,

      // OSM Search
      addressSearchQuery: "",
      searchingOSM: false,
      osmSuggestions: [],

      // Map
      zoom: 13,
      center: latLng(40.8518, 14.2681),
      url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
      attribution: '&copy; OpenStreetMap contributors',
      roadSearchText: ""
    };
  },

  computed: {
    contextStore() {
      return contextStore;
    },
    selectedLotto: {
      get() {
        return contextStore.activeCommessaId || "1";
      },
      set(val) {
        contextStore.setCommessa(val);
      }
    },
    commesse() {
      return contextStore.commesse;
    },
    filteredRoads() {
      if (!this.roadSearchText) return this.roads;
      const q = this.roadSearchText.toLowerCase().trim();
      return this.roads.filter(
        r =>
          (r.nome && r.nome.toLowerCase().includes(q)) ||
          (r.tronco && r.tronco.toLowerCase().includes(q))
      );
    },
    startLatLng() {
      if (this.roadForm.lat_start && this.roadForm.lon_start) {
        return latLng(this.roadForm.lat_start, this.roadForm.lon_start);
      }
      return null;
    },
    endLatLng() {
      if (this.roadForm.lat_end && this.roadForm.lon_end) {
        return latLng(this.roadForm.lat_end, this.roadForm.lon_end);
      }
      return null;
    },
    polylinePath() {
      const path = [];
      if (this.startLatLng) path.push(this.startLatLng);
      if (this.endLatLng) path.push(this.endLatLng);
      return path;
    }
  },

  async mounted() {
    if (this.tipologia !== "B" && this.tipologia !== "D") {
      this.$router.push({ name: "home" });
    } else {
      await contextStore.fetchCommesse();
      this.fetchRoads();
    }
  },

  watch: {
    mode(newMode) {
      if (newMode !== 'read') {
        this.$nextTick(() => {
          setTimeout(() => {
            if (this.$refs.roadMap && this.$refs.roadMap.mapObject) {
              this.$refs.roadMap.mapObject.invalidateSize();
            }
          }, 200);
        });
      }
    },
    'contextStore.activeCommessaId'() {
      this.fetchRoads();
    }
  },

  methods: {
    fetchCommesse() {
      return contextStore.fetchCommesse();
    },

    getCommessaNome(lottoId) {
      const found = this.commesse.find(c => String(c.id) === String(lottoId));
      return found ? found.nome : 'Commessa ' + lottoId;
    },

    fetchRoads() {
      this.loadingRoads = true;
      this.axios
        .get(`/admin/strade?lotto=${this.selectedLotto}`)
        .then(response => {
          this.roads = Array.isArray(response.data) ? response.data : [];
        })
        .catch(error => {
          console.error("Errore durante il caricamento delle strade:", error);
          this.$swal.fire("Errore", "Impossibile caricare l'elenco delle strade.", "error");
        })
        .finally(() => {
          this.loadingRoads = false;
        });
    },

    setAddMode() {
      this.roadForm = {
        id: null,
        nome: "",
        lotto: this.selectedLotto,
        tronco: "",
        lat_start: 40.8518,
        lon_start: 14.2681,
        lat_end: null,
        lon_end: null
      };
      this.coordType = "point";
      this.clickTarget = "start";
      this.center = latLng(40.8518, 14.2681);
      this.zoom = 13;
      this.mode = "add";
    },

    setEditMode(road) {
      this.roadForm = {
        id: road.id,
        nome: road.nome,
        lotto: road.lotto.toString(),
        tronco: road.tronco || "",
        lat_start: road.lat_start,
        lon_start: road.lon_start,
        lat_end: road.lat_end,
        lon_end: road.lon_end
      };
      const hasEnd = road.lat_end && road.lon_end;
      const isDifferent = hasEnd && (Math.abs(road.lat_start - road.lat_end) > 0.00001 || Math.abs(road.lon_start - road.lon_end) > 0.00001);
      this.coordType = isDifferent ? "segment" : "point";
      this.clickTarget = "start";
      if (road.lat_start && road.lon_start) {
        this.center = latLng(road.lat_start, road.lon_start);
        this.zoom = 15;
      }
      this.mode = "edit";
    },

    setCoordType(type) {
      this.coordType = type;
      if (type === 'segment' && (!this.roadForm.lat_end || !this.roadForm.lon_end)) {
        this.roadForm.lat_end = parseFloat((parseFloat(this.roadForm.lat_start) + 0.001).toFixed(6));
        this.roadForm.lon_end = parseFloat((parseFloat(this.roadForm.lon_start) + 0.001).toFixed(6));
        this.clickTarget = "end";
      }
    },

    isCoordMissing(value) {
      // Distingue "coordinata assente" da "coordinata = 0" (0 è un valore valido).
      return value === null || value === undefined || value === "" || Number.isNaN(value);
    },

    onCoordInput(field, rawValue) {
      // Normalizza il separatore decimale locale (virgola → punto) e converte a Number.
      // Non arrotonda in fase di digitazione: conserva il decimale completo digitato.
      if (rawValue === null || rawValue === undefined) {
        this.roadForm[field] = null;
        return;
      }
      const normalized = String(rawValue).trim().replace(",", ".");
      if (normalized === "") {
        this.roadForm[field] = null;
        return;
      }
      const parsed = parseFloat(normalized);
      this.roadForm[field] = Number.isNaN(parsed) ? null : parsed;
    },

    handleMapClick(e) {
      const lat = parseFloat(e.latlng.lat.toFixed(6));
      const lon = parseFloat(e.latlng.lng.toFixed(6));
      if (this.clickTarget === 'start') {
        this.roadForm.lat_start = lat;
        this.roadForm.lon_start = lon;
        if (this.coordType === 'segment') this.clickTarget = 'end';
      } else {
        this.roadForm.lat_end = lat;
        this.roadForm.lon_end = lon;
      }
    },

    handleStartDrag(e) {
      if (e && e.lat) {
        this.roadForm.lat_start = parseFloat(e.lat.toFixed(6));
        this.roadForm.lon_start = parseFloat(e.lng.toFixed(6));
      }
    },

    handleEndDrag(e) {
      if (e && e.lat) {
        this.roadForm.lat_end = parseFloat(e.lat.toFixed(6));
        this.roadForm.lon_end = parseFloat(e.lng.toFixed(6));
      }
    },

    searchOSMAddress() {
      if (!this.addressSearchQuery || this.addressSearchQuery.trim().length < 3) return;
      this.searchingOSM = true;
      this.osmSuggestions = [];
      fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(this.addressSearchQuery)}&limit=5&addressdetails=1&countrycodes=it`, {
        headers: { 'Accept-Language': 'it' }
      })
        .then(res => res.json())
        .then(data => {
          this.osmSuggestions = data || [];
          if (this.osmSuggestions.length === 0) {
            this.$swal.fire("Nessun Risultato", "Nessun indirizzo corrisponde alla ricerca.", "info");
          }
        })
        .catch(err => {
          console.error("OSM Nominatim error:", err);
          this.$swal.fire("Errore Ricerca", "Impossibile collegarsi al servizio di ricerca OSM.", "error");
        })
        .finally(() => {
          this.searchingOSM = false;
        });
    },

    selectOSMSuggestion(sug) {
      const address = sug.address || {};
      const roadName = address.road || address.pedestrian || address.suburb || sug.display_name.split(',')[0];
      this.roadForm.nome = roadName;
      const lat = parseFloat(sug.lat);
      const lon = parseFloat(sug.lon);
      this.roadForm.lat_start = parseFloat(lat.toFixed(6));
      this.roadForm.lon_start = parseFloat(lon.toFixed(6));
      if (sug.boundingbox && sug.boundingbox.length === 4) {
        const latMin = parseFloat(sug.boundingbox[0]);
        const latMax = parseFloat(sug.boundingbox[1]);
        const lonMin = parseFloat(sug.boundingbox[2]);
        const lonMax = parseFloat(sug.boundingbox[3]);
        if (Math.abs(latMax - latMin) > 0.0001 || Math.abs(lonMax - lonMin) > 0.0001) {
          this.coordType = "segment";
          this.roadForm.lat_start = parseFloat(latMin.toFixed(6));
          this.roadForm.lon_start = parseFloat(lonMin.toFixed(6));
          this.roadForm.lat_end = parseFloat(latMax.toFixed(6));
          this.roadForm.lon_end = parseFloat(lonMax.toFixed(6));
          this.clickTarget = "end";
        } else {
          this.coordType = "point";
          this.roadForm.lat_end = null;
          this.roadForm.lon_end = null;
          this.clickTarget = "start";
        }
      } else {
        this.coordType = "point";
        this.roadForm.lat_end = null;
        this.roadForm.lon_end = null;
        this.clickTarget = "start";
      }
      this.center = latLng(lat, lon);
      this.zoom = 16;
      this.osmSuggestions = [];
      this.addressSearchQuery = "";
    },

    saveRoad() {
      if (!this.roadForm.nome || !this.roadForm.lotto) {
        this.$swal.fire("Attenzione", "I campi Nome e Commessa sono obbligatori.", "warning");
        return;
      }
      if (this.isCoordMissing(this.roadForm.lat_start) || this.isCoordMissing(this.roadForm.lon_start)) {
        this.$swal.fire("Attenzione", "Imposta la posizione geografica del cantiere.", "warning");
        return;
      }
      if (this.coordType === 'segment' && (this.isCoordMissing(this.roadForm.lat_end) || this.isCoordMissing(this.roadForm.lon_end))) {
        this.$swal.fire("Attenzione", "Imposta le coordinate di fine del segmento.", "warning");
        return;
      }

      this.submitting = true;
      const formData = new FormData();
      if (this.roadForm.id) formData.append("id", this.roadForm.id);
      formData.append("nome", this.roadForm.nome);
      formData.append("lotto", this.roadForm.lotto);
      formData.append("tronco", this.roadForm.tronco);
      formData.append("lat_start", this.roadForm.lat_start);
      formData.append("lon_start", this.roadForm.lon_start);
      if (this.coordType === 'segment') {
        formData.append("lat_end", this.roadForm.lat_end);
        formData.append("lon_end", this.roadForm.lon_end);
      } else {
        formData.append("lat_end", this.roadForm.lat_start);
        formData.append("lon_end", this.roadForm.lon_start);
      }

      const url = this.mode === 'add' ? '/admin/strade' : '/admin/strade/update';
      this.axios
        .post(url, formData)
        .then(response => {
          if (response.data && response.data.success) {
            this.$swal.fire(
              "Salvato!",
              this.mode === 'add' ? "Nuovo cantiere aggiunto correttamente." : "Cantiere aggiornato con successo.",
              "success"
            );
            this.mode = "read";
            this.fetchRoads();
          } else {
            this.$swal.fire("Errore", response.data.error || "Impossibile salvare i dati.", "error");
          }
        })
        .catch(error => {
          console.error("Save road error:", error);
          this.$swal.fire("Errore", "Impossibile inviare i dati al server.", "error");
        })
        .finally(() => {
          this.submitting = false;
        });
    },

    deleteRoad(item) {
      this.$swal.fire({
        title: "Elimina cantiere?",
        text: `Vuoi davvero eliminare il cantiere "${item.nome}"? Questa azione è irreversibile.`,
        icon: "warning",
        showCancelButton: true,
        customClass: {
          confirmButton: "roadme-confirm roadme-danger"
        },
        confirmButtonText: "Sì, elimina!",
        cancelButtonText: "Annulla"
      }).then(result => {
        if (result.value) {
          const formData = new FormData();
          formData.append("id", item.id);
          this.axios
            .post("/admin/strade/delete", formData)
            .then(response => {
              if (response.data && response.data.success) {
                this.$swal.fire("Eliminato!", "Il cantiere è stato eliminato.", "success");
                this.fetchRoads();
              } else {
                this.$swal.fire("Impossibile eliminare", response.data.error || "Il cantiere è legato ad altre risorse.", "error");
              }
            })
            .catch(error => {
              console.error("Delete road error:", error);
              this.$swal.fire("Errore", "Si è verificato un errore durante l'eliminazione.", "error");
            });
        }
      });
    }
  }
};
</script>

<style scoped>
/* =========================================
   ADMIN STRADE — Premium UI (RoadME DS)
   ========================================= */

/* ── Shared tokens from style.md ── */
.cw-section-title {
  font-size: 0.82rem; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.08em;
  color: #64748b; margin-bottom: 22px;
  display: flex; align-items: center;
}
.cw-step-num {
  width: 22px; height: 22px; border-radius: 50%;
  background: linear-gradient(135deg, #F7931E, #E07B00);
  color: #fff; font-size: 0.7rem; font-weight: 800;
  display: inline-flex; align-items: center; justify-content: center;
  margin-right: 10px; flex-shrink: 0;
}
.cw-label {
  font-size: 0.75rem; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.07em;
  color: #94a3b8; margin-bottom: 8px; display: block;
}
.cw-input-wrap { position: relative; display: flex; align-items: center; }
.cw-field-icon {
  position: absolute; left: 12px;
  color: #94a3b8; font-size: 1rem; z-index: 2; pointer-events: none;
}
.cw-input {
  width: 100%; height: 46px; padding: 0 14px 0 36px;
  border: 1.5px solid #e2e8f0; border-radius: 10px;
  background: #f8fafc; font-size: 0.92rem; font-weight: 500; color: #1e293b;
  transition: border-color 0.2s, box-shadow 0.2s; outline: none;
}
.cw-input:focus {
  border-color: #F7931E; box-shadow: 0 0 0 3px rgba(247,147,30,0.12); background: #fff;
}
.cw-select-wrap { position: relative; display: flex; align-items: center; }
.cw-select {
  width: 100%; height: 46px; padding: 0 38px 0 36px;
  border: 1.5px solid #e2e8f0; border-radius: 10px;
  background: #f8fafc; appearance: none; -webkit-appearance: none;
  font-size: 0.92rem; font-weight: 500; color: #1e293b;
  cursor: pointer; transition: border-color 0.2s, box-shadow 0.2s; outline: none;
}
.cw-select:focus {
  border-color: #F7931E; box-shadow: 0 0 0 3px rgba(247,147,30,0.12); background: #fff;
}
.cw-select-arrow {
  position: absolute; right: 14px;
  color: #94a3b8; font-size: 1rem; pointer-events: none;
}
.cw-location-tabs {
  display: flex; background: #f1f5f9; border-radius: 12px; padding: 4px; gap: 2px;
}
.cw-loc-tab {
  flex: 1; display: flex; flex-direction: column; align-items: center;
  justify-content: center; gap: 3px; padding: 10px 6px; border: none;
  border-radius: 9px; background: transparent; color: #94a3b8;
  font-size: 0.7rem; font-weight: 700; text-transform: uppercase;
  letter-spacing: 0.04em; cursor: pointer; transition: all 0.2s;
}
.cw-loc-tab .mdi { font-size: 1.1rem; }
.cw-loc-tab:hover { background: rgba(255,255,255,0.7); color: #F7931E; }
.cw-loc-tab.active { background: #fff; color: #E07B00; box-shadow: 0 2px 8px rgba(0,0,0,0.08); }
.cw-alert-info {
  background: #FEF1E0; border-radius: 10px;
  padding: 12px 16px; border: 1px solid #FBD9AE;
}
.cw-chip {
  display: inline-block; padding: 3px 12px; border-radius: 20px;
  font-size: 0.75rem; font-weight: 600;
  background: #f1f5f9; color: #64748b; border: 1.5px solid #e2e8f0;
  transition: all 0.15s; user-select: none;
}
.cw-chip-active {
  background: linear-gradient(135deg, #F7931E, #E07B00); color: #fff; border-color: transparent;
}
.cw-footer {
  background: #fff; border-radius: 14px;
  padding: 18px 24px; border: 1px solid rgba(0,0,0,0.05);
  box-shadow: 0 2px 12px rgba(0,0,0,0.05);
}

/* ── Page-specific ── */

/* Header icon */
.au-header-icon {
  width: 48px; height: 48px; border-radius: 14px;
  background: linear-gradient(135deg, #F7931E, #E07B00);
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 4px 12px rgba(247,147,30,0.35); flex-shrink: 0;
}
.au-header-icon .mdi { color: #fff; font-size: 1.5rem; }

/* Card */
.au-card {
  background: #fff; border-radius: 16px;
  box-shadow: 0 2px 16px rgba(0,0,0,0.07);
  padding: 28px; border: 1px solid rgba(0,0,0,0.05);
}

/* Submit btn */
.au-btn-submit {
  display: inline-flex; align-items: center;
  padding: 10px 24px; border: none; border-radius: 30px;
  background: linear-gradient(135deg, #F7931E, #E07B00);
  color: #fff; font-size: 0.88rem; font-weight: 700;
  cursor: pointer; box-shadow: 0 4px 14px rgba(247,147,30,0.35);
  transition: all 0.2s;
}
.au-btn-submit:hover:not(:disabled) {
  background: linear-gradient(135deg, #E07B00, #C96A00);
  box-shadow: 0 6px 18px rgba(247,147,30,0.45); transform: translateY(-1px);
}
.au-btn-submit:disabled { opacity: 0.55; cursor: not-allowed; transform: none; }

/* Search */
.au-search-wrap {
  position: relative; display: flex; align-items: center;
  background: #f8fafc; border: 1.5px solid #e2e8f0;
  border-radius: 30px; height: 44px; padding: 0 16px 0 38px;
  transition: border-color 0.2s;
}
.au-search-wrap:focus-within { border-color: #F7931E; background: #fff; }
.au-search-icon { position: absolute; left: 14px; color: #94a3b8; font-size: 1rem; pointer-events: none; }
.au-search-input {
  border: none; background: transparent; outline: none;
  font-size: 0.9rem; color: #1e293b; width: 100%;
}

/* Table */
.au-table { width: 100%; border-collapse: collapse; }
.au-table thead tr { border-bottom: 2px solid #f1f5f9; }
.au-table thead th {
  padding: 10px 16px; font-size: 0.72rem; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.07em; color: #94a3b8; background: #f8fafc;
}
.au-table tbody tr { border-bottom: 1px solid #f1f5f9; transition: background 0.15s; }
.au-table tbody tr:hover { background: #f8fafc; }
.au-table td { padding: 14px 16px; vertical-align: middle; }

/* Road icon */
.as-road-icon {
  width: 34px; height: 34px; border-radius: 9px;
  background: #FEF1E0; color: #F7931E;
  display: flex; align-items: center; justify-content: center;
  font-size: 1rem; flex-shrink: 0;
}

/* Role/commessa badge (reused) */
.au-role-badge {
  display: inline-block; padding: 3px 10px; border-radius: 20px;
  font-size: 0.72rem; font-weight: 700; letter-spacing: 0.04em;
}
.au-role-admin { background: #FEF1E0; color: #E07B00; }

/* Action buttons */
.au-btn-action {
  display: inline-flex; align-items: center;
  padding: 6px 14px; border-radius: 20px; border: 1.5px solid #e2e8f0;
  background: #fff; color: #F7931E; font-size: 0.78rem; font-weight: 700;
  cursor: pointer; transition: all 0.15s;
}
.au-btn-action:hover { background: #FEF1E0; border-color: #F7931E; }
.au-btn-danger-sm {
  display: inline-flex; align-items: center;
  padding: 6px 14px; border-radius: 20px; border: 1.5px solid #fecaca;
  background: #fff5f5; color: #991b1b; font-size: 0.78rem; font-weight: 700;
  cursor: pointer; transition: all 0.15s;
}
.au-btn-danger-sm:hover { background: #fee2e2; border-color: #ef4444; }

/* OSM Search */
.as-osm-search-wrap { position: relative; }
.road-dropdown-wrapper { position: relative; }
.road-dropdown-menu {
  position: absolute; top: 100%; left: 0; right: 0;
  background: #fff; border: 1px solid #e2e8f0; border-top: none;
  border-radius: 0 0 10px 10px; max-height: 220px; overflow-y: auto;
  z-index: 1050; box-shadow: 0 8px 24px rgba(0,0,0,0.1);
}
.road-dropdown-item {
  padding: 8px 12px; cursor: pointer; font-size: 0.9rem; color: #334155;
}
.road-dropdown-item:hover { background: #FEF1E0; color: #C96A00; }

/* OSM search button inside input */
.as-osm-search-btn {
  position: absolute; right: 6px; top: 50%; transform: translateY(-50%);
  display: inline-flex; align-items: center;
  padding: 6px 16px; border: none; border-radius: 20px;
  background: linear-gradient(135deg, #F7931E, #E07B00);
  color: #fff; font-size: 0.8rem; font-weight: 700;
  cursor: pointer; transition: all 0.15s; z-index: 3;
}
.as-osm-search-btn:hover:not(:disabled) { background: linear-gradient(135deg, #E07B00, #C96A00); }
.as-osm-search-btn:disabled { opacity: 0.6; cursor: not-allowed; }

/* Map */
.as-map-wrapper {
  min-height: 320px; border-radius: 12px;
  overflow: hidden; border: 1.5px solid #e2e8f0;
  position: relative; z-index: 1;
}

/* Marker pin */
.marker-pin {
  width: 30px; height: 30px;
  border-radius: 50% 50% 50% 0; position: absolute;
  transform: rotate(-45deg); left: 50%; top: 50%;
  margin: -15px 0 0 -15px;
  display: flex; justify-content: center; align-items: center;
  box-shadow: 0 2px 5px rgba(0,0,0,0.3);
}
.marker-pin span { transform: rotate(45deg); }

/* Monospace coords */
.font-monospace { font-family: 'SFMono-Regular', Consolas, monospace; }
</style>
